/* Accumulated dividends.
 *
 * A single payment tells you nothing. What this screen is for is the shape of
 * the income across years: whether it is growing, what each month brings, and
 * what the shares that pay it actually cost — which is the only yield figure
 * that stays honest once the price has moved. */

import { useEffect, useMemo, useState } from 'react';
import { summariseDividends, type DividendSummary } from '../state/dividends';
import { ratesFor } from '../rules/tax';
import { accountName } from '../lib/accounts';
import {
  daysUntil, money, moneyExact, pct, pctSigned, plural, shares as sharesCount, sharesOf,
  shortDate,
} from '../lib/format';
import { newId } from '../lib/id';
import { decimalOr } from '../lib/number';
import { sharesOn } from '../state/sharesOn';
import { TabBar } from '../components/TabBar';
import { HideToggle } from '../components/HideToggle';
import { useStore } from '../state/store';
import type { Account, Dividend, DividendHistory } from '../types';
import type { Nav } from '../App';

export function DividendsScreen({ nav }: { nav: Nav }) {
  const { state, book } = useStore();

  const currency = state.settings.displayCurrency;
  const fx = state.fx;
  const toDisplay = (usd: number) => (currency === 'PLN' && fx?.usdPln ? usd * fx.usdPln : usd);
  const [adding, setAdding] = useState(false);
  /* The payment being corrected, if any. One form does both jobs — a second
     would be a second place for the same arithmetic to drift. */
  const [editing, setEditing] = useState<Dividend | null>(null);

  /* Which accounts sit outside the tax base. Passed in rather than looked up
     inside the summary, so the arithmetic stays a pure function of what it is
     given and the one place that knows what an account is stays this screen. */
  const shieldedAccounts = useMemo(
    () => new Set(state.data.accounts.filter((a) => a.taxShielded).map((a) => a.id)),
    [state.data.accounts],
  );

  const summary = useMemo(() => summariseDividends({
    dividends: state.data.dividends,
    fx,
    costByTicker: new Map(book.positions.map((p) => [p.ticker, p.costBasis])),
    sharesByTicker: new Map(book.positions.map((p) => [p.ticker, p.shares])),
    shieldedAccounts,
  }), [state.data.dividends, fx, book.positions, shieldedAccounts]);

  return (
    <div className="screen" data-tabbed="true">
      <div className="screen-head">
        <h1>Dividends</h1>
        <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }}
                onClick={() => setAdding(true)}>
          Record one
        </button>
        <HideToggle />
      </div>

      {/* What the companies are due to pay comes from the market, not from the
          record, so it does not wait on the record existing. It used to live
          inside the summary — which only renders once a payment has been
          entered — and so the one part of this screen that works on an empty
          book was the part you could not see until it was not empty. */}
      <ComingUp toDisplay={toDisplay} currency={currency} />

      {summary.paymentCount === 0 ? (
        <div className="card card-pad">
          <p style={{ fontSize: 14, lineHeight: 1.55 }}>Nothing recorded yet.</p>
          <p className="dim" style={{ fontSize: 13, lineHeight: 1.5, marginTop: 8 }}>
            Record each payment as it lands and this becomes a record of what the book pays you,
            year by year. The app can read the per-share amounts and dates from the market; how
            many shares you held on the day is the part only you know.
          </p>
          <button className="btn btn-primary btn-block" style={{ marginTop: 16 }}
                  onClick={() => setAdding(true)}>
            Record the first one
          </button>

          <div className="divider" />
          <EmptyPreview />
        </div>
      ) : (
        <Summary summary={summary} toDisplay={toDisplay} currency={currency} />
      )}

      {summary.paymentCount > 0 ? (
        <PaymentLog toDisplay={toDisplay} currency={currency} onEdit={setEditing} />
      ) : null}

      {adding || editing ? (
        <AddDividend
          {...(editing ? { existing: editing } : {})}
          onClose={() => { setAdding(false); setEditing(null); }}
        />
      ) : null}

      <TabBar current="dividends" go={nav.go} />
    </div>
  );
}

/** What the screen becomes once payments exist. Drawn faintly and labelled, so
    it reads as an example rather than as a record of nothing. */
function EmptyPreview() {
  const shape = [38, 52, 47, 71, 90];
  return (
    <div className="div-preview" aria-hidden="true">
      <div className="fin-title">What this becomes</div>
      <div className="fin-bars" style={{ height: 64 }}>
        {shape.map((h, i) => (
          <div key={i} className="fin-bar-slot">
            <span className="fin-bar" style={{ height: `${h}%`, top: `${100 - h}%`, background: 'var(--up)' }} />
          </div>
        ))}
      </div>
      <div className="fin-labels">
        {shape.map((_, i) => (
          <div key={i} className="fin-label"><span className="k">year {i + 1}</span></div>
        ))}
      </div>
      <div className="dim" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.45 }}>
        One bar per year, a strip of what each month brought, and a per-company list with yield
        measured against what you paid. It fills in from the first payment you record.
      </div>
    </div>
  );
}

/* ---------------- the summary ---------------- */

/* Drawn on its own so the screen can show it before a single payment has been
   recorded. What is still to come, as against what has already landed —
   everything else on this screen is a record of money received, and this is the
   only part that is a date in the future, so it says which is which. */
function UpcomingSection({
  upcoming, annualUsd, onCostPct, shelteredUsd, afterTaxUsd, dividendPct, toDisplay, currency,
}: {
  upcoming: Array<{
    ticker: string; date: string; kind: string; ahead: boolean;
    perShare: number | null; currency: string; shares: number; shelteredShares: number;
    onYourShares: number | null; shelteredPay: number;
  }>;
  annualUsd: number;
  onCostPct: number;
  /** The part of it that lands somewhere outside the tax base. */
  shelteredUsd: number;
  /** What would be left after the local charge, or null with no rate set. */
  afterTaxUsd: number | null;
  dividendPct: number | null;
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
}) {
  /* Yahoo publishes the *last* date a company went ex, not the next one — the
     next is not announced until it is. So the heading follows what is actually
     in the rows: calling a date in July "coming up" in August would be the app
     saying something it has no basis for. */
  const anyAhead = upcoming.some((u) => u.ahead);

  return (
    <section>
      <div className="section-head">
        <h2>{anyAhead ? 'Coming up' : 'What your holdings pay'}</h2>
        <span className="meta num">
          {money(toDisplay(annualUsd), currency)}
          {afterTaxUsd === null ? '' : ` → ${money(toDisplay(afterTaxUsd), currency)}`} a year
        </span>
      </div>
      <div className="card card-pad">
        {upcoming.map((u, i) => (
          <div key={u.ticker} style={{ marginBottom: i === upcoming.length - 1 ? 0 : 12 }}>
            <div className="kv">
              <span className="k" style={{ fontWeight: 600, color: 'var(--text)' }}>
                {u.ticker}
              </span>
              <span className="dim" style={{ fontSize: 11 }}>
                {u.ahead ? 'next' : 'last'} {u.kind} {shortDate(u.date)}
                {u.ahead ? ` · in ${daysUntil(u.date)} days` : ''}
              </span>
              <span className="v num" style={{ marginLeft: 'auto' }}>
                {u.onYourShares === null ? '—' : money(toDisplay(u.onYourShares), currency)}
              </span>
            </div>
            {u.perShare !== null ? (
              <div className="dim num" style={{ fontSize: 11 }}>
                {moneyExact(u.perShare, u.currency as never)} per share a year
                {u.shares > 0 ? ` × ${sharesCount(u.shares)}` : ''}
                {/* Named per row, because one company can sit on both sides of
                    the line and the row is where somebody is looking when they
                    wonder which. */}
                {u.shelteredShares > 0 ? (
                  <span className="up">
                    {' · '}
                    {u.shelteredShares >= u.shares
                      ? 'all sheltered'
                      : `${sharesCount(u.shelteredShares)} sheltered`}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
        <strong>{money(toDisplay(annualUsd), currency)} a year</strong> declared at today&rsquo;s
        rates, which is {pct(onCostPct)} of what those positions cost you.
        {afterTaxUsd === null
          ? ' Set the rate on dividends under Taxes and what would be left after it appears beside that figure.'
          : shelteredUsd > 0
            ? ` After ${pct(dividendPct!)} on the part outside a sheltered account, `
              + `${money(toDisplay(afterTaxUsd), currency)} of it is yours; `
              + `${money(toDisplay(shelteredUsd), currency)} lands somewhere nothing further is charged on.`
            : ` After ${pct(dividendPct!)}, ${money(toDisplay(afterTaxUsd), currency)} of it is yours. `
              + 'None of these shares sit in a sheltered account.'} Measured against cost, not
        against price, the only yield that stays honest as a holding ages. It is a projection
        and not a promise: a company can cut its dividend, and this is only as good as the rate
        currently published.
        <br />
        Each figure is the annual rate, not one payment. Most companies split it into four.
        {anyAhead ? '' : ' Every date below has already passed: an exchange publishes the last '
          + 'one a company went ex, and the next is not published until it is announced. '
          + 'That a date is old is not a sign the dividend stopped.'}
      </div>
    </section>
  );
}

/* What the companies you hold are due to pay. It comes from the calendar call
   the app already makes for earnings dates, so it costs nothing extra.
 *
 * Its own component because it answers a different question from the rest of
 * the screen: everything else here is money that has landed, and this is a date
 * still ahead. A book with no payments recorded still has companies with
 * dividend dates, and this is the part that can say so on day one. */
function ComingUp({ toDisplay, currency }: {
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
}) {
  const { state, book } = useStore();

  const shieldedIds = new Set(state.data.accounts.filter((a) => a.taxShielded).map((a) => a.id));

  const upcoming = book.positions
    .map((p) => {
      const row = state.earnings.find((e) => e.symbol === p.yahooSymbol)?.dividend;
      if (!row) return null;
      const date = row.exDate ?? row.payDate;
      if (!date) return null;
      const perShare = row.perShare && row.perShare > 0 ? row.perShare : null;
      /* The same company can be held in two accounts with different answers,
         and this book has exactly that: HSAI sits in an IKE and in a taxable
         one. Summing the position and applying one rate to the whole would be
         wrong for both halves, so the shares are split where they actually
         sit. */
      const shelteredShares = p.holdings
        .filter((h) => shieldedIds.has(h.account))
        .reduce((sum, h) => sum + h.shares, 0);
      return {
        ticker: p.ticker,
        date,
        kind: row.exDate ? 'ex-date' : 'payment',
        ahead: date >= new Date().toISOString().slice(0, 10),
        perShare,
        currency: p.currency,
        shares: p.shares,
        shelteredShares,
        onYourShares: perShare === null ? null : perShare * p.shares,
        shelteredPay: perShare === null ? 0 : perShare * shelteredShares,
      };
    })
    .filter((u): u is NonNullable<typeof u> => u !== null)
    .sort((a, b) => Number(b.ahead) - Number(a.ahead) || a.date.localeCompare(b.date));

  /* What the book would pay over a year at today's rates. A projection, not a
     promise: a company can cut it, and the figure is only as good as the rate
     the exchange currently publishes. Measured against cost rather than against
     price, which is the only yield that stays honest as a holding ages. */
  const annualUsd = upcoming.reduce((sum, u) => sum + (u.onYourShares ?? 0), 0);
  const shelteredUsd = upcoming.reduce((sum, u) => sum + u.shelteredPay, 0);
  const costOfPayers = upcoming.reduce((sum, u) => {
    const p = book.positions.find((x) => x.ticker === u.ticker);
    return sum + (p?.costBasis ?? 0);
  }, 0);
  const onCostPct = costOfPayers > 0 ? (annualUsd / costOfPayers) * 100 : 0;

  /* What would be left after tax, at the rate set under Taxes.
   *
   * A flat rate on the taxable part and nothing on the sheltered part. This is
   * the whole charge rather than only the top-up: under a regime that credits
   * foreign withholding, what the payer's country takes and what you owe here
   * add up to the rate, so applying the rate once to the declared amount is the
   * total burden however it is split between the two.
   *
   * Null with no rate set. A projection with a guessed rate in it would be two
   * guesses stacked, and the second one ends up on a tax return. */
  const rates = ratesFor(state.settings);
  const afterTaxUsd = rates.dividendPct === null
    ? null
    : shelteredUsd + (annualUsd - shelteredUsd) * (1 - rates.dividendPct / 100);

  if (upcoming.length === 0) return null;

  return (
    <UpcomingSection
      upcoming={upcoming} annualUsd={annualUsd} onCostPct={onCostPct}
      shelteredUsd={shelteredUsd} afterTaxUsd={afterTaxUsd} dividendPct={rates.dividendPct}
      toDisplay={toDisplay} currency={currency}
    />
  );
}

/* Where the payments landed, and what that means for what is still owed.
 *
 * The tax on a dividend is not one number: some of it was already taken by the
 * payer's country before the money arrived, and the rest is charged where you
 * file. Which of those apply turns entirely on the account. A sheltered one is
 * finished when the money lands. Any other still has the local charge to come,
 * with the withholding credited against it where the regime allows.
 *
 * That is why this is a split and not a footnote: the same $100 declared is a
 * different amount kept depending on which broker it went to, and a screen that
 * tots them together hides the one thing this section exists to show.
 *
 * With no rate set the charge is not guessed. It says so, because "no rate set"
 * and "nothing to pay" are different answers and only one of them is true. */
function WhereItLanded({ summary, toDisplay, currency }: {
  summary: DividendSummary;
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
}) {
  const { state } = useStore();
  const rates = ratesFor(state.settings);
  const { sheltered, taxable } = summary;

  // Nothing to split when everything sits on one side of the line.
  if (sheltered.count === 0 || taxable.count === 0) {
    return (
      <div className="dim" style={{ fontSize: 11, marginTop: 10, lineHeight: 1.45 }}>
        {sheltered.count > 0
          ? 'Every payment here landed in a sheltered account, so nothing further is charged on any of it.'
          : rates.dividendPct === null
            ? 'None of this is in a sheltered account. Set the rate on dividends under Taxes and this will show what is still owed on it.'
            : `None of this is in a sheltered account, so the ${pct(rates.dividendPct)} charge applies to all of it.`}
      </div>
    );
  }

  const due = (split: typeof taxable) => {
    if (rates.dividendPct === null) return null;
    const charge = Math.max(0, split.grossUsd) * (rates.dividendPct / 100);
    const credit = rates.creditsWithholding ? Math.min(split.withheldUsd, charge) : 0;
    return Math.max(0, charge - credit);
  };

  const stillOwed = due(taxable);

  return (
    <>
      <div className="divider" />
      <div className="section-head" style={{ marginTop: 0, marginBottom: 6 }}>
        <h2 style={{ fontSize: 12 }}>Where it landed</h2>
      </div>
      <div className="kv-list">
        <div className="kv">
          <span className="k">
            Sheltered
            <span className="dim" style={{ marginLeft: 6 }}>{plural(sheltered.count, 'payment')}</span>
          </span>
          <span className="v num">{money(toDisplay(sheltered.netUsd), currency)}</span>
        </div>
        <div className="kv">
          <span className="k">
            Taxable
            <span className="dim" style={{ marginLeft: 6 }}>{plural(taxable.count, 'payment')}</span>
          </span>
          <span className="v num">{money(toDisplay(taxable.netUsd), currency)}</span>
        </div>
        <div className="kv">
          <span className="k">Still owed on the taxable half</span>
          <span className="v num">
            {stillOwed === null ? 'no rate set' : money(toDisplay(stillOwed), currency)}
          </span>
        </div>
        <div className="kv">
          <span className="k" style={{ fontWeight: 600, color: 'var(--text)' }}>Kept</span>
          <span className="v num" style={{ fontWeight: 600 }}>
            {stillOwed === null
              ? money(toDisplay(summary.totalNetUsd), currency)
              : money(toDisplay(summary.totalNetUsd - stillOwed), currency)}
          </span>
        </div>
      </div>
      <div className="dim" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
        {stillOwed === null
          ? 'Set the rate on dividends under Taxes and the charge on the taxable half appears here. Until then "Kept" is only what landed.'
          : rates.creditsWithholding
            ? `The charge is ${pct(rates.dividendPct!)} on what was declared, with what the payer's country already withheld credited against it. Nothing further is charged on the sheltered half.`
            : `The charge is ${pct(rates.dividendPct!)} on what was declared for the taxable half. Nothing further is charged on the sheltered half.`}
      </div>
    </>
  );
}

function Summary({ summary, toDisplay, currency }: {
  summary: DividendSummary;
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
}) {
  const [span, setSpan] = useState<'12m' | 'all'>('all');
  const months = span === '12m' ? summary.byMonth.slice(-12) : summary.byMonth.slice(-24);

  return (
    <>
      <div className="card card-pad">
        <div className="hero-label">Received, after what was withheld</div>
        <div className="hero-total num">{money(toDisplay(summary.totalNetUsd), currency)}</div>
        <div className="dim" style={{ fontSize: 12.5, marginTop: 6 }}>
          {plural(summary.paymentCount, 'payment')} over {summary.yearsRunning} years
          {summary.firstPayDate ? `, since ${shortDate(summary.firstPayDate)}` : ''}
        </div>

        <div className="divider" />

        {/* The three figures the record already holds, in the order money
            actually moves through them. Only the last was ever shown, which
            answers the smallest of the three questions somebody has: it says
            what landed and not what was declared or what was taken. */}
        <div className="kv-list">
          <div className="kv">
            <span className="k">Declared</span>
            <span className="v num">{money(toDisplay(summary.totalGrossUsd), currency)}</span>
          </div>
          <div className="kv">
            <span className="k">Withheld at source</span>
            <span className="v num down">
              {summary.totalTaxUsd > 0 ? `-${money(toDisplay(summary.totalTaxUsd), currency)}` : money(0, currency)}
            </span>
          </div>
          <div className="kv">
            <span className="k" style={{ fontWeight: 600, color: 'var(--text)' }}>Landed</span>
            <span className="v num" style={{ fontWeight: 600 }}>
              {money(toDisplay(summary.totalNetUsd), currency)}
            </span>
          </div>
        </div>

        <WhereItLanded summary={summary} toDisplay={toDisplay} currency={currency} />

        <div className="divider" />

        <div className="perp-strip" style={{ padding: 0 }}>
          <div>
            <div className="k">Last 12 months</div>
            <div className="v num">{money(toDisplay(summary.trailingTwelveUsd), currency)}</div>
          </div>
          <div>
            <div className="k">Per month</div>
            <div className="v num">{moneyExact(toDisplay(summary.monthlyAverageUsd), currency)}</div>
          </div>
          <div>
            <div className="k">Yield on cost</div>
            <div className="v num up">
              {summary.yieldOnCostPct === null ? '—' : pct(summary.yieldOnCostPct)}
            </div>
          </div>
        </div>

        <div className="dim" style={{ fontSize: 11, marginTop: 12, lineHeight: 1.45 }}>
          Yield on cost measures the last twelve months against what you paid for the shares, not
          against what they are worth now. It is the number that grows as a holding ages.

        </div>
      </div>

      {/* The growth story: one bar per year. */}
      {summary.byYear.length > 0 ? (
        <section>
          <div className="section-head">
            <h2>By year</h2>
            {summary.forwardAnnualUsd > 0 ? (
              <span className="meta num">
                next 12m ≈ {money(toDisplay(summary.forwardAnnualUsd), currency)}
              </span>
            ) : null}
          </div>
          <div className="card card-pad">
            <YearBars summary={summary} toDisplay={toDisplay} currency={currency} />
            {summary.forwardAnnualUsd > 0 ? (
              <div className="dim" style={{ fontSize: 11, marginTop: 12, lineHeight: 1.45 }}>
                The forward figure assumes each holding keeps paying what it paid over the last
                year, at the share count you hold today. It is an assumption, not a promise.
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* What each month actually brought, dry months included. */}
      <section>
        <div className="section-head">
          <h2>By month</h2>
          <div className="seg" style={{ flex: '0 0 auto' }}>
            {(['12m', 'all'] as const).map((s) => (
              <button key={s} aria-pressed={span === s} onClick={() => setSpan(s)}>
                {s === '12m' ? '12 months' : '24 months'}
              </button>
            ))}
          </div>
        </div>
        <div className="card card-pad">
          <MonthBars months={months} toDisplay={toDisplay} currency={currency} />
        </div>
      </section>

      <section>
        <div className="section-head"><h2>By company</h2></div>
        <div className="rows">
          {summary.byTicker.map((t) => (
            <div key={t.ticker} className="card card-pad">
              <div className="row-top">
                <span className="row-ticker">{t.ticker}</span>
                <span className="row-right">
                  <span className="row-value num">{money(toDisplay(t.netUsd), currency)}</span>
                  <span className="row-weight num" style={{ display: 'block' }}>
                    {t.yieldOnCostPct === null ? 'cost unknown' : `${pct(t.yieldOnCostPct)} on cost`}
                  </span>
                </span>
              </div>
              <div className="row-meta">
                <span>{plural(t.count, 'payment')}</span>
                <span className="num">last 12m {money(toDisplay(t.trailingTwelveUsd), currency)}</span>
                <span>since {shortDate(t.firstPayDate)}</span>
                <span>last {shortDate(t.lastPayDate)}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function YearBars({ summary, toDisplay, currency }: {
  summary: DividendSummary; toDisplay: (usd: number) => number; currency: 'USD' | 'PLN';
}) {
  const hi = Math.max(...summary.byYear.map((y) => y.netUsd), 0) || 1;
  return (
    <div className="fin-chart">
      <div className="fin-bars" style={{ height: 92 }}>
        {summary.byYear.map((y) => (
          <div key={y.year} className="fin-bar-slot">
            <span className="fin-bar" style={{
              height: Math.max((y.netUsd / hi) * 92, 2),
              top: 92 - Math.max((y.netUsd / hi) * 92, 2),
              background: 'var(--up)',
            }} />
          </div>
        ))}
      </div>
      <div className="fin-labels">
        {summary.byYear.map((y, i) => {
          const prior = summary.byYear[i - 1]?.netUsd;
          const growth = prior && prior > 0 ? ((y.netUsd - prior) / prior) * 100 : null;
          return (
            <div key={y.year} className="fin-label">
              <span className="v num">{money(toDisplay(y.netUsd), currency)}</span>
              <span className="k">{y.year}</span>
              {growth !== null ? <span className="n num">{pctSigned(growth)}</span> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthBars({ months, toDisplay, currency }: {
  months: Array<{ month: string; netUsd: number }>;
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
}) {
  if (months.length === 0) return <div className="empty">Nothing yet.</div>;
  const hi = Math.max(...months.map((m) => m.netUsd), 0) || 1;
  const best = months.reduce((a, b) => (b.netUsd > a.netUsd ? b : a), months[0]!);

  return (
    <>
      <div className="month-bars">
        {months.map((m) => (
          <div key={m.month} className="month-slot"
               title={`${m.month}: ${moneyExact(toDisplay(m.netUsd), currency)}`}>
            <span className="month-bar" style={{
              height: `${m.netUsd > 0 ? Math.max((m.netUsd / hi) * 100, 4) : 1}%`,
              background: m.netUsd > 0 ? 'var(--accent)' : 'var(--edge2)',
            }} />
            <span className="month-tick">{m.month.slice(5)}</span>
          </div>
        ))}
      </div>
      <div className="row-meta" style={{ marginTop: 12 }}>
        <span className="num">best {m2(best.month)} · {money(toDisplay(best.netUsd), currency)}</span>
        <span>{months.filter((m) => m.netUsd === 0).length} months paid nothing</span>
      </div>
    </>
  );
}

function m2(month: string): string {
  return new Date(`${month}-01T00:00:00Z`)
    .toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

/* ---------------- the log ---------------- */

function PaymentLog({ toDisplay, currency, onEdit }: {
  toDisplay: (usd: number) => number;
  currency: 'USD' | 'PLN';
  onEdit: (d: Dividend) => void;
}) {
  const { state, removeDividend } = useStore();
  const rows = [...state.data.dividends].sort((a, b) => b.payDate.localeCompare(a.payDate));

  return (
    <section>
      <div className="section-head">
        <h2>Every payment</h2>
        <span className="meta">{rows.length}</span>
      </div>
      <div className="rows">
        {rows.map((d) => (
          <div key={d.id} className="card card-pad">
            <div className="row-top">
              <span className="row-ticker" style={{ fontSize: 13.5 }}>{d.ticker}</span>
              <span className="dim" style={{ fontSize: 11.5 }}>{shortDate(d.payDate)}</span>
              <span className="row-right">
                <span className="num" style={{ fontWeight: 600 }}>
                  {moneyExact(toDisplay(d.rateToUsd ? d.net * d.rateToUsd : d.net), currency)}
                </span>
              </span>
              <button className="btn btn-ghost btn-sm" aria-label={`Remove ${d.ticker} payment`}
                      onClick={() => void removeDividend(d.id)}>×</button>
            </div>
            <div className="row-meta">
              <span className="num">{sharesOf(d.shares, d.ticker)} × {d.perShare}</span>
              <span className="num">gross {moneyExact(d.gross, d.currency as 'USD')}</span>
              {d.taxWithheld > 0 ? (
                <span className="num">tax {moneyExact(d.taxWithheld, d.currency as 'USD')}</span>
              ) : null}
              <span>{accountName(state.data.accounts, d.account)}</span>
            </div>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }}
                    onClick={() => onEdit(d)}>
              Correct this payment
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- recording one ---------------- */

function AddDividend({ existing, onClose }: { existing?: Dividend; onClose: () => void }) {
  const { state, book, putDividend, loadDividendHistory, setCash } = useStore();
  const accounts = state.data.accounts;
  const positions = book.positions;

  const [ticker, setTicker] = useState(existing?.ticker ?? positions[0]?.ticker ?? '');
  const position = positions.find((p) => p.ticker === ticker);
  const [account, setAccount] = useState<Account>(
    existing?.account ?? position?.accounts[0] ?? accounts[0]?.id ?? '');
  const [payDate, setPayDate] = useState(existing?.payDate ?? new Date().toISOString().slice(0, 10));
  const [shares, setShares] = useState(existing ? String(existing.shares) : '');
  const [perShare, setPerShare] = useState(existing ? String(existing.perShare) : '');
  const [tax, setTax] = useState(existing ? String(existing.taxWithheld) : '');
  const [history, setHistory] = useState<DividendHistory | null>(null);
  const [loading, setLoading] = useState(false);

  // Offer the ticker's own payment history, so the date and the per-share
  // amount are picked rather than typed from memory.
  useEffect(() => {
    if (!position) { setHistory(null); return; }
    let live = true;
    setLoading(true);
    void loadDividendHistory(position.yahooSymbol).then((h) => {
      if (!live) return;
      setHistory(h);
      setLoading(false);
    });
    return () => { live = false; };
  }, [position, loadDividendHistory]);

  /* The lot this payment landed in, and what its own trades say it held on the
     day. `Dividend.shares` is documented as the count held on the pay date, and
     the offer used to be the count held *today* — right for a payment recorded
     the week it arrived, wrong for one entered after buying more of the same
     company, and wrong silently. See `state/sharesOn.ts`. */
  const lot = position?.holdings.find((h) => h.account === account);
  const onThatDay = useMemo(
    () => (lot ? sharesOn(state.data.trades, lot, payDate) : null),
    [lot, state.data.trades, payDate],
  );

  useEffect(() => {
    // Only when starting fresh: a payment being corrected already carries the
    // count that was held on the day, and today's lot is a different number.
    if (existing) return;
    if (!lot) return;
    setShares(String(onThatDay?.shares ?? lot.shares));
  }, [existing, lot, onThatDay]);

  const sharesNum = decimalOr(shares);
  const perShareNum = decimalOr(perShare);
  const taxNum = decimalOr(tax);
  const gross = sharesNum * perShareNum;
  const net = gross - taxNum;
  const listingCurrency = (position?.quote?.currency ?? position?.currency ?? 'USD') as string;

  const save = async () => {
    if (!position || gross <= 0) return;
    const record: Dividend = {
      id: existing?.id ?? newId('div'),
      ticker: position.ticker,
      account,
      payDate,
      shares: sharesNum,
      perShare: perShareNum,
      gross: Number(gross.toFixed(6)),
      taxWithheld: taxNum,
      net: Number(net.toFixed(6)),
      currency: listingCurrency,
    };
    if (listingCurrency === 'USD') record.rateToUsd = 1;
    else if (listingCurrency === 'PLN' && state.fx?.usdPln) record.rateToUsd = 1 / state.fx.usdPln;
    await putDividend(record);

    /* A new payment moves the cash balance, because the money landed. A
       correction does not: the money landed once, and adding the net a second
       time would invent a deposit. If a correction changed the amount, the
       balance is yours to adjust under Money in and out — which is the same
       rule as everywhere else here, that correcting a record is not re-running
       what it records. */
    if (!existing) {
      const balance = book.cashByAccount.find((c) => c.account === account);
      if (balance && balance.currency === listingCurrency) {
        await setCash(account, balance.amount + record.net, balance.currency);
      }
    }
    onClose();
  };

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" aria-labelledby="div-title">
      <div className="modal" style={{ maxWidth: 420, maxHeight: '86vh', overflowY: 'auto' }}>
        <h2 id="div-title">{existing ? 'Correcting a payment' : 'Record a dividend'}</h2>
        {existing ? (
          <div className="note" style={{ marginTop: 10 }}>
            Changing these figures restates the record. It does not move the cash balance. The
            money landed once, and adding it again would invent a deposit.
          </div>
        ) : null}

        <div className="field" style={{ marginTop: 16 }}>
          <label htmlFor="d-ticker">Company</label>
          <select id="d-ticker" className="select" value={ticker}
                  onChange={(e) => { setTicker(e.target.value); setPerShare(''); }}>
            {positions.map((p) => <option key={p.ticker} value={p.ticker}>{p.ticker}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="dim" style={{ fontSize: 12, marginBottom: 12 }}>Looking up its payment history…</div>
        ) : history && history.payments.length > 0 ? (
          <div className="field">
            <label>Its recent payments · tap to fill</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {history.payments.slice(-8).reverse().map((p) => (
                <button key={p.date} className="tag" style={{ cursor: 'pointer', padding: '5px 8px' }}
                        onClick={() => { setPayDate(p.date); setPerShare(String(p.perShare)); }}>
                  {shortDate(p.date)} · {p.perShare}
                </button>
              ))}
            </div>
            <div className="hint">
              Dates and per-share amounts come from the market. The share count is yours to confirm.
            </div>
          </div>
        ) : history ? (
          <div className="note" style={{ marginBottom: 14 }}>
            No dividend history on record for {ticker}. Enter the payment by hand.
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="d-account">Account it landed in</label>
          <select id="d-account" className="select" value={account}
                  onChange={(e) => setAccount(e.target.value)}>
            {(position?.accounts ?? accounts.map((a) => a.id)).map((id) => (
              <option key={id} value={id}>{accountName(accounts, id)}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="d-date">Pay date</label>
            <input id="d-date" className="input" type="date" value={payDate}
                   onChange={(e) => setPayDate(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="d-per">Per share</label>
            <input id="d-per" className="input" data-mono="true" inputMode="decimal"
                   value={perShare} onChange={(e) => setPerShare(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="d-shares">Shares held on that date</label>
            <input id="d-shares" className="input" data-mono="true" inputMode="decimal"
                   value={shares} onChange={(e) => setShares(e.target.value)} />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="d-tax">Tax withheld</label>
            <input id="d-tax" className="input" data-mono="true" inputMode="decimal"
                   value={tax} onChange={(e) => setTax(e.target.value)} placeholder="0" />
          </div>
        </div>

        {/* Where the offered count came from, in the same voice the rest of the
            book uses about a figure it worked out: what it is, and what it is
            not. A correction is left alone — the record already carries the
            count that was held, and re-deriving it would overwrite the answer
            being corrected. */}
        {!existing && onThatDay ? (
          <div className="hint" style={{ marginTop: -6, marginBottom: 14 }}>
            {onThatDay.shares === null
              ? onThatDay.why === 'no-trades'
                ? `No trades recorded for this account, so this is what you hold today. `
                  + `If you held a different number on ${shortDate(payDate)}, change it.`
                : `Your trades account for ${sharesCount(onThatDay.netShares)} of the `
                  + `${sharesCount(onThatDay.heldNow)} in this account, so they cannot say what `
                  + `you held then. This is today's count.`
              : onThatDay.shares === 0
                ? `Your trades say you held none of it on ${shortDate(payDate)}.`
                : Math.abs(onThatDay.shares - onThatDay.heldNow) < 1e-6
                  ? 'From your trades for this account.'
                  : `From your trades: ${sharesCount(onThatDay.shares)} on `
                    + `${shortDate(payDate)}, against ${sharesCount(onThatDay.heldNow)} today.`}
          </div>
        ) : null}

        <div className="kv-list">
          <div className="kv">
            <span className="k">Gross</span>
            <span className="v num">{moneyExact(gross, listingCurrency as 'USD')}</span>
          </div>
          <div className="kv">
            <span className="k">Reaches the account</span>
            <span className="v num up">{moneyExact(net, listingCurrency as 'USD')}</span>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={gross <= 0} onClick={() => void save()}>
            Record it
          </button>
        </div>
      </div>
    </div>
  );
}
